import React, { useState } from "react";
import {
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { FormInput, FormTextarea, FormSelect } from "@/components";
import { toast } from "react-hot-toast";
import type {
  DataModel,
  DataField,
  FieldType,
} from "@/features/Projects/models/project.types";
import { ModelEditorProps } from "../schemaPhase.types";
import { FIELD_TYPE_OPTIONS } from "../schemaPhase.definitions";

export function ModelEditor({ model, onSave, onCancel }: ModelEditorProps) {
  const [editedModel, setEditedModel] = useState<DataModel>(
    model || {
      id: `model-${Date.now()}`,
      name: "",
      description: "",
      tableName: "",
      fields: [
        {
          id: "id",
          name: "id",
          type: "uuid",
          description: "Identificador único",
          validation: { required: true, unique: true, indexed: true },
          example: "550e8400-e29b-41d4-a716-446655440000",
        },
      ],
      relationships: [],
      timestamps: true,
      softDelete: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  );

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const handleModelChange = (field: keyof DataModel, value: any) => {
    setEditedModel((prev) => ({ ...prev, [field]: value }));
  };

  const handleFieldChange = (
    fieldIndex: number,
    field: keyof DataField,
    value: any
  ) => {
    const updatedFields = [...editedModel.fields];
    updatedFields[fieldIndex] = {
      ...updatedFields[fieldIndex],
      [field]: value,
    };
    setEditedModel((prev) => ({ ...prev, fields: updatedFields }));
  };

  const handleValidationChange = (
    fieldIndex: number,
    validation: keyof DataField["validation"],
    value: any
  ) => {
    const updatedFields = [...editedModel.fields];
    updatedFields[fieldIndex] = {
      ...updatedFields[fieldIndex],
      validation: {
        ...updatedFields[fieldIndex].validation,
        [validation]: value,
      },
    };
    setEditedModel((prev) => ({ ...prev, fields: updatedFields }));
  };

  const addField = () => {
    const newField: DataField = {
      id: `field-${Date.now()}`,
      name: "",
      type: "string",
      description: "",
      validation: { required: false },
      example: "",
    };
    setEditedModel((prev) => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
  };

  const removeField = (fieldIndex: number) => {
    if (editedModel.fields.length <= 1) {
      toast.error("Um modelo deve ter pelo menos um campo");
      return;
    }

    setEditedModel((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, index) => index !== fieldIndex),
    }));
    setShowDeleteConfirm(null);
  };

  const handleSave = () => {
    // Validações básicas
    if (!editedModel.name.trim()) {
      toast.error("Nome do modelo é obrigatório");
      return;
    }
    if (!editedModel.tableName.trim()) {
      toast.error("Nome da tabela é obrigatório");
      return;
    }

    // Validar campos
    for (let i = 0; i < editedModel.fields.length; i++) {
      const field = editedModel.fields[i];
      if (!field.name.trim()) {
        toast.error(`Nome do campo na posição ${i + 1} é obrigatório`);
        return;
      }
    }

    // Gerar tableName automaticamente se não fornecido
    const finalModel = {
      ...editedModel,
      tableName:
        editedModel.tableName ||
        editedModel.name.toLowerCase().replace(/\s+/g, "_"),
      updatedAt: new Date().toISOString(),
    };

    onSave(finalModel);
  };

  return (
    <div className="bg-white border-2 border-blue-200 rounded-lg p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-900">
          {model ? "Editar Modelo" : "Novo Modelo"}
        </h4>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            leftIcon={<XMarkIcon className="h-4 w-4" />}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            leftIcon={<CheckIcon className="h-4 w-4" />}
          >
            Salvar
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Informações básicas do modelo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Nome do Modelo"
            placeholder="Ex: User, Product, Order"
            value={editedModel.name}
            onChange={(e) => handleModelChange("name", e.target.value)}
            required
          />
          <FormInput
            label="Nome da Tabela"
            placeholder="Ex: users, products, orders"
            value={editedModel.tableName}
            onChange={(e) => handleModelChange("tableName", e.target.value)}
            required
          />
        </div>

        <FormTextarea
          label="Descrição"
          placeholder="Descreva o propósito deste modelo..."
          value={editedModel.description}
          onChange={(e) => handleModelChange("description", e.target.value)}
          rows={2}
        />

        {/* Configurações */}
        <div className="flex space-x-6">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={editedModel.timestamps}
              onChange={(e) =>
                handleModelChange("timestamps", e.target.checked)
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Timestamps (createdAt, updatedAt)
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={editedModel.softDelete}
              onChange={(e) =>
                handleModelChange("softDelete", e.target.checked)
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Soft Delete (deletedAt)
            </span>
          </label>
        </div>

        {/* Campos */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-gray-900">Campos</h5>
            <Button
              variant="outline"
              size="sm"
              onClick={addField}
              leftIcon={<PlusIcon className="h-4 w-4" />}
            >
              Adicionar Campo
            </Button>
          </div>

          <div className="space-y-3">
            {editedModel.fields.map((field, index) => (
              <div
                key={field.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h6 className="font-medium text-gray-800">
                    Campo #{index + 1}
                  </h6>
                  {editedModel.fields.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(field.id)}
                      leftIcon={<TrashIcon className="h-4 w-4" />}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remover
                    </Button>
                  )}
                </div>

                {/* Confirmação de remoção */}
                {showDeleteConfirm === field.id && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                    <p className="text-sm text-red-800 mb-2">
                      Tem certeza que deseja remover o campo "
                      {field.name || "Sem nome"}"?
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeField(index)}
                      >
                        Sim, remover
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <FormInput
                    label="Nome"
                    placeholder="Ex: email, name, age"
                    value={field.name}
                    onChange={(e) =>
                      handleFieldChange(index, "name", e.target.value)
                    }
                    required
                  />
                  <FormSelect
                    label="Tipo"
                    value={field.type}
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        "type",
                        e.target.value as FieldType
                      )
                    }
                    options={FIELD_TYPE_OPTIONS}
                    required
                  />
                  <FormInput
                    label="Exemplo"
                    placeholder="Valor de exemplo"
                    value={field.example || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "example", e.target.value)
                    }
                  />
                </div>

                <div className="mt-3">
                  <FormTextarea
                    label="Descrição"
                    placeholder="Descreva este campo..."
                    value={field.description}
                    onChange={(e) =>
                      handleFieldChange(index, "description", e.target.value)
                    }
                    rows={2}
                  />
                </div>

                {/* Validações */}
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validações
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={field.validation?.required || false}
                        onChange={(e) =>
                          handleValidationChange(
                            index,
                            "required",
                            e.target.checked
                          )
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Obrigatório</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={field.validation?.unique || false}
                        onChange={(e) =>
                          handleValidationChange(
                            index,
                            "unique",
                            e.target.checked
                          )
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Único</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={field.validation?.indexed || false}
                        onChange={(e) =>
                          handleValidationChange(
                            index,
                            "indexed",
                            e.target.checked
                          )
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Indexado</span>
                    </label>
                    <FormInput
                      label="Tamanho Mín."
                      type="number"
                      placeholder="0"
                      value={field.validation?.min || ""}
                      onChange={(e) =>
                        handleValidationChange(
                          index,
                          "min",
                          parseInt(e.target.value) || undefined
                        )
                      }
                    />
                    <FormInput
                      label="Tamanho Máx."
                      type="number"
                      placeholder="255"
                      value={field.validation?.max || ""}
                      onChange={(e) =>
                        handleValidationChange(
                          index,
                          "max",
                          parseInt(e.target.value) || undefined
                        )
                      }
                    />
                    <FormInput
                      label="Padrão (RegEx)"
                      placeholder="^[a-zA-Z]+$"
                      value={field.validation?.pattern || ""}
                      onChange={(e) =>
                        handleValidationChange(index, "pattern", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}